import React, { useContext, useEffect, useState } from "react";
import ReactModal from "react-modal";
import { browserVersion, isMobile } from "react-device-detect";
import { useHistory, useParams } from "react-router-dom";

import { Visible } from "../../../core/components";
import { Collapsible } from "../../../components";
import { Button, Icon, IconButton } from "../../../components/ui";
import { useStoreWriteGuard } from "../../../helpers/storeScope";
import { SettingsContext } from "../../../contexts";
import { useItemState } from "../../hooks";
import Quote from "../Quote";
import QuoteWorkspace from "../QuoteWorkspace";
import QuoteRoster from "../QuoteWorkspace/QuoteRoster";
import { quoteWorkspaceEnabled } from "../../hooks/quoteWorkspace";
import QuoteList from "./QuoteList";
import Trigger from "./Trigger";
import { SectionTrigger } from "../../../orderManagement/molecules/SectionTrigger";
import BubbleView from "../../../core/components/Bubble/BubbleView";
import "./rc-quote-list.scss";

const QuotesView = ({
  item,
  onQuoteSaved,
  quoteId,
  selectQuote,
  refreshList,
  onSelectedQuote,
  step,
  toggleShowButton,
  type,
  setType,
  navigateOnSave = true,
  // Rendered inside the expanded section above the quote list — the request
  // flow's "Before you continue" checklist mounts here so it sits with the
  // list it describes rather than above the collapsible trigger.
  beforeList = null,
  // The Orders right-hand panel. The disclosure made sense when quotes were
  // nested in a list row and a card could not be allowed to grow; in a panel
  // that exists ONLY to show one order's quotes it is a bar you have to click
  // to see the thing you already asked for. In panel mode the list renders
  // open, the trigger bar is gone (its section title lives in the panel
  // header), and NEW QUOTE sits with the panel's other commands instead of in
  // the list toolbar.
  panel = false,
  // A per-row ACTION dropdown in the quote list — the flow's SELECT ORDER/SELL QUOTE sections
  // pass this so a quote can be approved / sent / marked accepted / turned into a listing from
  // the row, instead of only from inside the quote editor. Distinct from `onQuoteSaved`
  // because a row action must NOT open the editor on the quote it just changed.
  onRowQuoteSaved,
}) => {
  const { isMerchant, iframeQueryParams } = useContext(SettingsContext);
  const params = useParams();
  const history = useHistory();
  // A quote belongs to a store; from "Master Store" there is no single one to
  // save it against. See useStoreWriteGuard.
  const { blocked: storeGuardBlocked, reason: storeGuardReason } = useStoreWriteGuard();
  const basePath = history.location.pathname
    .split("/")
    .slice(1, 3)
    .join("/");
  const { isSelectingQuotes, setSelectedQuote, selectedQuote } = useItemState();
  // DAO setup = the quote opens in a modal over the list (unchanged). New setup =
  // no pop-ups: the quote opens IN PLACE of the list, with a way back to it.
  const inlineQuote = quoteWorkspaceEnabled();
  // Bumped on every save so the quote list re-reads the quote's new stage.
  const [saves, setSaves] = useState(0);

  const onClose = () => {
    if (panel) {
      selectQuote(null);
    } else if (basePath === "requests/orders") {
      history.push(`/${basePath}?scrollTo=${item.id}&${iframeQueryParams}`);
    } else {
      selectQuote(null);
    }
  };

  const toggleExpanded = () => {
    if (params.requestId == item.id) {
      history.push(`/${basePath}?${iframeQueryParams}`);
    } else {
      history.push(`/${basePath}/${item.id}?${iframeQueryParams}`);
    }
  };

  // In the panel the list is ALWAYS open. Elsewhere it follows the URL, which is what kept the
  // panel's own list shut: the panel opens from local state, so `params.requestId` was either
  // absent or still pointing at a different order, and the section read as collapsed until you
  // clicked the bar — asking you to reveal the one thing the panel exists to show.
  const isExpanded = panel || params.requestId == item.id || isSelectingQuotes;

  const collapsibleProps = step
    ? {
        trigger: <SectionTrigger label={step.label} icon="sendQuote" />,
        open: step.open,
        testId: "confirm-request",
        handleTriggerClick: () => toggleShowButton(),
      }
    : {
        trigger: <Trigger item={item} />,
        handleTriggerClick: () => toggleExpanded(),
        open: isExpanded,
      };

  const onOpenQuote = (id, quote) => {
    if (isMerchant !== !!quote.merchant_id) {
      // Do not open the quote when it wasn't created in the current user's scope
      return;
    }

    selectQuote && selectQuote(id);
  };

  const quoteSaved = (quote) => {
    // A stage change (approve, send, accept…) only refreshes the quote itself;
    // the list above the workspace shows the stage too.
    if (inlineQuote) setSaves((n) => n + 1);
    onQuoteSaved(quote);
    setSelectedQuote(quote);
    // Panel: a NEW quote stays open in the editor as the quote it just became
    // (the URL is not touched there — see navigateOnSave below).
    if (panel && quote?.id && String(quoteId) !== String(quote.id)) {
      selectQuote(quote.id);
    }
  };

  useEffect(() => {
    onSelectedQuote && selectedQuote && onSelectedQuote(selectedQuote);
  }, [selectedQuote]);

  const newQuote = () => {
    // Inside the request flow (`step` present) the modal IS the
    // editor — selectQuote("new") opens it, and navigating away
    // would remount the route (for a PX sell section, onto the
    // SELL sibling's URL, which re-renders the whole deal with
    // the trade-in as a phantom order slot). The full-page
    // ai-extraction editor remains the Orders-page behaviour.
    selectQuote("new");
    if (!step) {
      history.push(
        `/${basePath}/${item.id}/new/ai-extraction?${iframeQueryParams}`
      );
    }
  };

  // New setup: every quote for the watch stays listed (coloured by what it can
  // be for this request) with the open quote's workspace under it — no pop-up.
  const body = inlineQuote ? (
    <Visible when={isExpanded}>
      {beforeList}
      <div className="qws-stack">
        <QuoteRoster
          item={item}
          type={type}
          refresh={`${refreshList}-${saves}`}
          openId={quoteId}
          onOpen={(id, quote) =>
            String(id) === String(quoteId) ? onClose() : onOpenQuote(id, quote)
          }
          chosenId={isSelectingQuotes ? selectedQuote?.id : undefined}
          onChoose={isSelectingQuotes ? setSelectedQuote : undefined}
          // In the panel, NEW QUOTE lives in the panel header.
          onNew={panel ? undefined : newQuote}
          newDisabled={storeGuardBlocked}
          newTitle={storeGuardReason}
        />
        {!!quoteId && (
          <QuoteWorkspace
            key={quoteId}
            id={quoteId}
            item={item}
            onQuoteSaved={quoteSaved}
            onBack={onClose}
            navigateOnSave={navigateOnSave && !panel}
          />
        )}
      </div>
    </Visible>
  ) : (
    <Visible when={isExpanded}>
      {beforeList}
        <div className="quote-list">
          <QuoteList
            item={item}
            refresh={refreshList}
            selectItem={(id, quote) => onOpenQuote(id, quote)}
            chosenQuote={isSelectingQuotes ? selectedQuote : undefined}
            chooseQuote={isSelectingQuotes ? setSelectedQuote : undefined}
            onlyApproved={isSelectingQuotes}
            type={type}
            setType={setType}
            rowActions={!!onRowQuoteSaved}
            onRowQuoteSaved={onRowQuoteSaved}
            // In the panel these commands live in the panel header instead — NEW QUOTE beside
            // VIEW, where the order's other commands are, and no listing-sync icon: it is the
            // one control here that acts on the LISTING rather than on a quote, and sitting in
            // the quotes bar as a bare red/green arrow it read as a broken status light.
            headerAction={panel ? null : (
              <>
                {item.orderListing?.length > 0 && (
                  <IconButton
                    size="sm"
                    label="Sync quotes"
                    onClick={() => selectQuote("select-listing")}
                    data-btn="quote-listing-open"
                  >
                    <Icon name="RefreshCw" size="sm" />
                  </IconButton>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<Icon name="Plus" size="sm" />}
                  data-btn="quote-new"
                  disabled={storeGuardBlocked}
                  title={storeGuardReason}
                  onClick={newQuote}
                >
                  New quote
                </Button>
              </>
            )}
          />
        </div>

        <ReactModal
          {...{
            isOpen: !!quoteId,
            onRequestClose: onClose,
            className: `product-preview-modal ${
              browserVersion < 15 && !isMobile
                ? "old-mac-product-preview-modal"
                : ""
            }`,
          }}
        >
          {!!quoteId && (
            <>
              <IconButton
                label="Close"
                className="quote-editor-close"
                onClick={onClose}
                data-btn="quote-editor-close"
              >
                <Icon name="X" size="sm" />
              </IconButton>

              <Quote
                id={quoteId}
                item={item}
                onQuoteSaved={quoteSaved}
                // Panel: never push /requests/orders/:id/:quoteId after creating a
                // quote. That URL matches a different <Route>, re-mounting the
                // Orders page — which dropped the panel AND the editor on SAVE.
                navigateOnSave={navigateOnSave && !panel}
                // `params.requestId` is empty in the panel — it opens from local state, not
                // from the URL — so the order comes off the item itself.
                basePath={`/${basePath}/${params.requestId || item.id}/${quoteId}`}
              />
            </>
          )}
        </ReactModal>
    </Visible>
  );

  // The panel IS the disclosure. A second one inside it was a bar you had to click to reveal
  // the only thing the panel renders. Its section title lives in the panel header now.
  if (panel) return body;

  return (
    <Collapsible {...collapsibleProps} noContainer={!step}>
      {body}
    </Collapsible>
  );
};

export default QuotesView;
